import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, CandlestickChart, LineChart, Loader2, BadgeCheck } from "lucide-react";
import Sparkline from "./Sparkline";
import OrderBook from "./OrderBook";
import CandleChart, { RANGES, chartFmt as fmt } from "./CandleChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FlashPrice({ value, dp }) {
  const prev = useRef(value);
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (prev.current !== value) {
      setFlash(value > prev.current ? "up" : "down");
      prev.current = value;
      const t = setTimeout(() => setFlash(null), 800);
      return () => clearTimeout(t);
    }
  }, [value]);
  return (
    <span
      className={`font-tabular text-sm transition-colors duration-500 ${
        flash === "up" ? "text-[#26A69A]" : flash === "down" ? "text-[#EF5350]" : "text-white/85"
      }`}
    >
      ${fmt(value, dp)}
    </span>
  );
}

function LiveDot({ active }) {
  return (
    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em]" data-testid="ws-indicator">
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#26A69A] animate-pulse-dot" : "bg-white/25"}`} />
      <span className={active ? "text-[#26A69A]" : "text-white/30"}>{active ? "Connected" : "Connecting"}</span>
    </span>
  );
}

function Logo({ src, alt, fallback }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-gold/30 bg-gold/[0.06] font-tabular text-[9px] text-gold">
        {fallback}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-7 w-7 shrink-0 rounded-full border border-white/10 bg-white/95 object-contain p-0.5"
    />
  );
}

export default function TerminalMarkets({ token, coins, stocks, cash, onTraded }) {
  const [selected, setSelected] = useState(null);
  const [selectedKind, setSelectedKind] = useState("crypto");
  const [listTab, setListTab] = useState("crypto");
  const [range, setRange] = useState("1d");
  const [mode, setMode] = useState("candles");
  const [chart, setChart] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const coin = useMemo(
    () => (coins || []).find((c) => c.symbol.toUpperCase() === selected),
    [coins, selected],
  );
  const stock = useMemo(
    () => (stocks || []).find((s) => s.symbol.toUpperCase() === selected),
    [stocks, selected],
  );
  const instrument = selectedKind === "stock" ? stock : coin;

  useEffect(() => {
    if ((coins && coins.length) || (stocks && stocks.length)) setLastSync(Date.now());
  }, [coins, stocks]);

  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    const load = (showSpinner) => {
      if (showSpinner) setChartLoading(true);
      axios
        .get(`${API}/markets/chart/${selected}?span=${range}`)
        .then(({ data }) => {
          if (cancelled) return;
          setChart(data);
          setLastSync(Date.now());
        })
        .catch(() => { if (!cancelled) setChart(null); })
        .finally(() => { if (!cancelled && showSpinner) setChartLoading(false); });
    };
    load(true);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load(false);
    }, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selected, range]);

  const trade = async (side) => {
    setBusy(side);
    setError(null);
    try {
      await axios.post(
        `${API}/terminal/trade`,
        { symbol: selected, side, qty: parseFloat(qty), order_type: "market" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setQty("");
      onTraded();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Trade failed.");
    } finally {
      setBusy(null);
    }
  };

  const connected = lastSync && Date.now() - lastSync < 90000;

  if (!selected) {
    const list = listTab === "crypto" ? coins || [] : stocks || [];
    return (
      <div className="mt-8 border border-white/10 bg-surface" data-testid="terminal-markets-list">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="flex items-center gap-2">
            {["crypto", "stocks"].map((t) => (
              <button
                key={t}
                data-testid={`market-tab-${t}`}
                onClick={() => setListTab(t)}
                className={`rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  listTab === t ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:text-gold"
                }`}
              >
                {t === "crypto" ? `Crypto ${coins?.length || ""}` : `Stocks ${stocks?.length || ""}`}
              </button>
            ))}
          </div>
          <LiveDot active={connected} />
        </div>
        {list.map((c, i) => {
          const isStock = listTab === "stocks";
          const chg = isStock ? c.change_pct : c.change_24h;
          const up = (chg ?? 0) >= 0;
          return (
            <button
              key={c.symbol}
              data-testid={`market-row-${String(c.symbol).toUpperCase()}`}
              onClick={() => { setSelected(String(c.symbol).toUpperCase()); setSelectedKind(isStock ? "stock" : "crypto"); setRange("1d"); setError(null); }}
              className="grid w-full grid-cols-[1fr_auto] items-center gap-4 border-b border-white/5 px-5 py-4 text-left transition-colors duration-300 last:border-b-0 hover:bg-white/[0.03] sm:grid-cols-[2.5rem_1.4fr_1fr_0.8fr_auto]"
            >
              <span className="hidden text-xs text-white/30 sm:inline">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex items-center gap-2.5">
                <Logo src={c.logo} alt={c.name} fallback={String(c.symbol).slice(0, 2).toUpperCase()} />
                <span className="flex items-baseline gap-2">
                  <span className="text-sm text-white">{String(c.symbol).toUpperCase()}</span>
                  <span className="hidden text-[10px] text-white/35 sm:inline">{c.name}</span>
                  {isStock && c.verified && <BadgeCheck className="h-3 w-3 text-gold" />}
                </span>
              </span>
              <FlashPrice value={c.price} dp={c.price < 1 ? 4 : 2} />
              <span className={`text-right font-tabular text-xs sm:text-left ${up ? "text-[#26A69A]" : "text-[#EF5350]"}`}>
                {up ? "+" : ""}
                {(chg ?? 0).toFixed(2)}%
              </span>
              {isStock ? <span /> : <Sparkline data={c.sparkline} positive={up} />}
            </button>
          );
        })}
      </div>
    );
  }

  const chg = selectedKind === "stock" ? instrument?.change_pct : instrument?.change_24h;
  const up = (chg ?? 0) >= 0;
  return (
    <div className="mt-8 border border-white/10 bg-surface" data-testid="terminal-coin-detail">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
        <button
          data-testid="coin-back-btn"
          onClick={() => setSelected(null)}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-gold"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Markets
        </button>
        <LiveDot active={connected} />
      </div>

      <div className="p-5 sm:p-8 lg:grid lg:grid-cols-[1fr_300px] lg:gap-8">
        <div>
          <div className="flex items-center gap-3">
            <Logo src={instrument?.logo} alt={instrument?.name || selected} fallback={selected.slice(0, 2)} />
            <p className="text-xs uppercase tracking-[0.25em] text-white/40" data-testid="coin-name">
              {instrument?.name || selected}
            </p>
          </div>
          <p className="mt-2 font-serif text-4xl font-light tracking-tight text-white sm:text-5xl" data-testid="coin-price">
            ${fmt(instrument?.price, (instrument?.price ?? 1) < 1 ? 4 : 2)}
          </p>
          <p className={`mt-1 font-tabular text-sm ${up ? "text-[#26A69A]" : "text-[#EF5350]"}`} data-testid="coin-change">
            {up ? "+" : ""}
            {(chg ?? 0).toFixed(2)}% · 24h
          </p>

          <div className="mt-6">
            <div className="relative">
              {chartLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60">
                  <Loader2 className="h-5 w-5 animate-spin text-gold" />
                </div>
              )}
              <CandleChart candles={chart?.candles} mode={mode} />
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2">
                <button
                  data-testid="mode-line"
                  onClick={() => setMode("line")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${
                    mode === "line" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
                  }`}
                >
                  <LineChart className="h-3.5 w-3.5" /> Line
                </button>
                <button
                  data-testid="mode-candles"
                  onClick={() => setMode("candles")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${
                    mode === "candles" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
                  }`}
                >
                  <CandlestickChart className="h-3.5 w-3.5" /> Candles
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {RANGES.map((r) => (
                  <button
                    key={r.k}
                    data-testid={`range-${r.l}`}
                    onClick={() => setRange(r.k)}
                    className={`rounded-full px-3.5 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                      range === r.k ? "bg-white/15 text-gold" : "text-white/45 hover:text-gold"
                    }`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(() => {
            const candleHigh = chart?.candles?.length ? Math.max(...chart.candles.map((c) => c[2])) : null;
            const candleLow = chart?.candles?.length ? Math.min(...chart.candles.map((c) => c[3])) : null;
            const hi = instrument?.high_24h ?? candleHigh;
            const lo = instrument?.low_24h ?? candleLow;
            return (
              <div className="mt-6 grid grid-cols-3 gap-4 border-t border-white/10 pt-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                    {selectedKind === "stock" ? "Range High" : "24h High"}
                  </p>
                  <p className="mt-1 font-tabular text-sm text-white/85" data-testid="coin-high">
                    {hi != null ? `$${fmt(hi, hi < 1 ? 4 : 2)}` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                    {selectedKind === "stock" ? "Range Low" : "24h Low"}
                  </p>
                  <p className="mt-1 font-tabular text-sm text-white/85" data-testid="coin-low">
                    {lo != null ? `$${fmt(lo, lo < 1 ? 4 : 2)}` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Category</p>
                  <p className="mt-1 text-sm uppercase tracking-[0.15em] text-white/85">
                    {selectedKind === "stock" ? "Equity" : "Crypto"}
                  </p>
                </div>
              </div>
            );
          })()}
          <div className="mt-5 border border-white/10 bg-ink p-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Available cash</p>
            <p className="mt-1 font-tabular text-2xl text-gold" data-testid="coin-cash">${fmt(cash)}</p>
          </div>
          {error && <p className="mt-3 text-xs text-[#FF003C]" data-testid="coin-trade-error">{error}</p>}
        </div>

        <div className="mt-8 lg:mt-0">
          {selectedKind === "crypto" ? (
            <OrderBook symbol={selected} />
          ) : (
            <div className="border border-white/10 bg-ink p-5" data-testid="stock-info-panel">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">About</p>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                {instrument?.name} — verified mega-cap listing on the Aureon board.
                Market orders fill at the live quote.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
